import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { useQuery } from "@tanstack/react-query";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { useCart } from "@/context/CartContext";
import { useFilter } from "@/context/FilterContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const EASE = [0.16, 1, 0.3, 1];

const fmt = (price) =>
  `$${Number.isInteger(price) ? price : price.toFixed(2)}`;

export const CATEGORY_GROUPS = {
  Home: ["Home Improvement", "Home & Garden", "Home Decor"],
  Pets: ["Petcare", "Pets"],
  Kitchen: ["Kitchen"],
  Bathroom: ["Bathroom", "Bath & Beauty"],
  Fans: ["name:fan"],
  Outdoor: ["Camping", "Automotive"],
  Baby: ["Kids & Babies"],
  Tech: ["Tech Accessories", "Audio & Video", "Mobile & Laptop Accessories"],
};

export const matchesGroup = (product, group) =>
  (CATEGORY_GROUPS[group] || []).some((rule) =>
    rule.startsWith("name:")
      ? product.name.toLowerCase().includes(rule.slice(5))
      : product.tag === rule
  );

export const ProductGallery = ({ product }) => {
  const images = product.images?.length ? product.images : [product.image];
  const [active, setActive] = useState(0);
  useEffect(() => setActive(0), [product?.id]);

  return (
    <div>
      <div className="aspect-square overflow-hidden bg-white sm:border-b-0">
        <img
          src={images[active]}
          alt={product.name}
          className="h-full w-full object-cover"
        />
      </div>
      {images.length > 1 && (
        <div className="flex flex-wrap gap-2 p-3">
          {images.map((src, i) => (
            <button
              key={`${src}-${i}`}
              data-testid={`gallery-thumb-${i}`}
              onClick={() => setActive(i)}
              className={`aspect-square w-[calc(25%-6px)] overflow-hidden border transition-colors duration-300 sm:w-auto sm:min-w-0 sm:flex-1 ${
                i === active ? "border-ember" : "border-line"
              }`}
            >
              <img
                src={src}
                alt={`${product.name} view ${i + 1}`}
                className="h-full w-full object-cover"
              />
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export const VariantPicker = ({ product, selectedId, onSelect }) => {
  const variants = (product.variants || []).filter(
    (v) => v.title && v.title !== "Default Title"
  );
  if (variants.length <= 1) return null;

  const label = (title) => title.replace(/\s*\([^)]*\)/g, "").trim();

  return (
    <div data-testid="variant-picker" className="mt-5 flex flex-wrap gap-2">
      {variants.map((v) => (
        <button
          key={v.id}
          data-testid={`variant-option-${v.id}`}
          disabled={!v.available}
          onClick={() => onSelect(v)}
          className={`border px-4 py-2 text-[11px] uppercase tracking-[0.15em] transition-colors duration-300 ${
            selectedId === v.id
              ? "border-ember bg-ember text-ink"
              : "border-line text-smoke hover:border-ink hover:text-ink"
          } ${v.available ? "" : "opacity-40 line-through"}`}
        >
          {label(v.title)}
        </button>
      ))}
    </div>
  );
};

export const QuickViewAddButton = ({ product, variant, onDone }) => {
  const { add, setOpen } = useCart();

  const handleAdd = () => {
    const v = variant || (product.variants && product.variants[0]) || null;
    const hasRealVariant = v && v.title && v.title !== "Default Title";
    const variantLabel = hasRealVariant
      ? v.title.replace(/\s*\([^)]*\)/g, "").trim()
      : null;
    add({
      ...product,
      id: v ? `${product.id}--${v.id}` : product.id,
      name: variantLabel ? `${product.name} — ${variantLabel}` : product.name,
      price: v ? v.price : product.price,
      variantId: v ? v.id : product.variantId,
    });
    onDone();
    toast(`${product.name} added to your bag`, {
      action: { label: "View Bag", onClick: () => setOpen(true) },
    });
  };

  return (
    <button
      data-testid="quickview-add-to-bag"
      onClick={handleAdd}
      className="bg-ink px-6 py-3 text-xs uppercase tracking-[0.25em] text-bone transition-colors duration-300 hover:bg-ember hover:text-ink"
    >
      Add to Bag
    </button>
  );
};

const ProductCard = ({ product, index }) => {
  const { add, setOpen } = useCart();
  const navigate = useNavigate();
  const soldOut = product.available === false;
  const isTouch =
    typeof window !== "undefined" &&
    window.matchMedia("(pointer: coarse)").matches;

  const handleAdd = (e) => {
    e.stopPropagation();
    add(product);
    toast(`${product.name} added to your bag`, {
      action: { label: "View Bag", onClick: () => setOpen(true) },
    });
  };

  return (
    <motion.article
      initial={{ opacity: 0, y: 40 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-80px" }}
      transition={{ duration: 0.8, delay: isTouch ? 0 : (index % 3) * 0.1, ease: EASE }}
      data-testid={`product-card-${product.id}`}
      className={`group transition-transform duration-500 ease-out hover:-translate-y-2 ${
        index % 3 === 1 ? "lg:mt-14" : ""
      }`}
    >
      <div
        className="relative aspect-square cursor-pointer overflow-hidden bg-white"
        onClick={() => navigate(`/product/${product.id}`)}
        data-testid={`product-quickview-${product.id}`}
      >
        <img
          src={product.image}
          alt={product.name}
          loading="lazy"
          decoding="async"
          className={`h-full w-full object-cover transition-transform duration-700 ease-out group-hover:rotate-1 group-hover:scale-105 ${
            soldOut ? "opacity-60 grayscale" : ""
          }`}
        />
        <span className="absolute left-4 top-4 bg-ember px-3 py-1 text-[10px] uppercase tracking-[0.2em] text-ink">
          {String(index + 1).padStart(3, "0")}
        </span>
        {soldOut ? (
          <div
            data-testid={`sold-out-${product.id}`}
            className="absolute inset-x-0 bottom-0 bg-line py-4 text-center text-xs uppercase tracking-[0.25em] text-smoke"
          >
            Sold Out
          </div>
        ) : (
          <button
            data-testid={`add-to-cart-${product.id}`}
            onClick={handleAdd}
            className="absolute inset-x-0 bottom-0 translate-y-0 bg-ink py-4 text-xs uppercase tracking-[0.25em] text-bone transition-all duration-300 hover:bg-ember hover:text-ink md:translate-y-full md:group-hover:translate-y-0"
          >
            Add to Bag — {fmt(product.price)}
          </button>
        )}
      </div>
      <div className="mt-3 flex items-start justify-between gap-3 md:mt-5 md:gap-4">
        <div>
          <h3 className="font-serif text-xl font-medium tracking-tight text-ink sm:text-2xl">
            {product.name}
          </h3>
          <p className="mt-1 hidden text-xs uppercase tracking-[0.15em] text-smoke sm:block">
            {product.notes}
          </p>
        </div>
        <span
          data-testid={`product-price-${product.id}`}
          className="pt-1 text-sm font-medium text-ink"
        >
          {fmt(product.price)}
        </span>
      </div>
    </motion.article>
  );
};

export default function Products() {
  const { data: products = [], isLoading } = useQuery({
    queryKey: ["products"],
    queryFn: async () => (await axios.get(`${API}/products`)).data,
  });
  const { category: activeCategory, setCategory: setActiveCategory } = useFilter();

  const categories = useMemo(() => {
    return [
      "All",
      ...Object.keys(CATEGORY_GROUPS).filter((group) =>
        products.some((p) => matchesGroup(p, group))
      ),
    ];
  }, [products]);

  const filtered = useMemo(
    () =>
      activeCategory === "All"
        ? products
        : products.filter((p) => matchesGroup(p, activeCategory)),
    [products, activeCategory]
  );

  return (
    <section
      id="shop"
      data-testid="products-section"
      className="mx-auto max-w-[1600px] px-5 py-12 md:px-12 md:py-28"
    >
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-120px" }}
        transition={{ duration: 0.9, ease: EASE }}
        className="mb-10 flex flex-wrap items-end justify-between gap-6 md:mb-24"
      >
        <div>
          <p className="text-xs uppercase tracking-[0.3em] text-smoke">
            The Catalogue
          </p>
          <h2 className="mt-4 font-serif text-4xl font-medium tracking-tight text-ink sm:text-5xl md:text-7xl">
            Shop the <span className="italic text-ember">goods</span>.
          </h2>
        </div>
        <p
          data-testid="products-count"
          className="text-xs uppercase tracking-[0.2em] text-smoke"
        >
          {isLoading ? "…" : `${String(filtered.length).padStart(2, "0")} objects`}
        </p>
      </motion.div>

      <div
        data-testid="category-filters"
        className="-mx-5 mb-8 flex gap-2 overflow-x-auto px-5 pb-2 md:-mx-12 md:mb-12 md:gap-3 md:px-12"
      >
        {categories.map((category) => (
          <button
            key={category}
            data-testid={`filter-chip-${category.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
            onClick={() => setActiveCategory(category)}
            className={`whitespace-nowrap border px-4 py-1.5 text-[10px] uppercase tracking-[0.18em] transition-colors duration-300 md:px-5 md:py-2 md:text-xs ${
              activeCategory === category
                ? "border-ember bg-ember text-ink"
                : "border-line bg-transparent text-smoke hover:border-ember hover:text-ember"
            }`}
          >
            {category}
          </button>
        ))}
      </div>

      {isLoading && (
        <p
          data-testid="products-loading"
          className="py-16 text-center text-xs uppercase tracking-[0.25em] text-smoke"
        >
          The store is waking up — give it a few seconds…
        </p>
      )}

      <div className="grid grid-cols-1 gap-x-8 gap-y-16 sm:grid-cols-2 lg:grid-cols-3">
        {filtered.map((product, i) => (
          <ProductCard
            key={product.id}
            product={product}
            index={i}
          />
        ))}
      </div>
    </section>
  );
}
